import React from 'react';
import { useTable, useFilters, useSortBy } from 'react-table';
import { useTranslation } from 'react-i18next';
import Icon from '@/components/common/Icon';
import { allocationsCss } from '@digital_monorepo/styling';
import generateExcel from 'zipcelx';

const AllocationsView = ({
  columns,
  data,
  filterOptions,
  selectedLevels,
  onLevelChange,
  projectFilter,
  onProjectFilterChange,
  uniqueDates,
  formatWeekHeader,
  getClientDataForWeek,
  showChargeability,
  onShowChargeabilityChange,
  includeSubs,
  onIncludeSubsChange,
  onClearFilters
}) => {
  const { t } = useTranslation();

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters } = useTable(
    {
      columns,
      data,
      autoResetFilters: false,
      autoResetSortBy: false
    },
    useFilters,
    useSortBy
  );

  const totalAllocations = rows.length;

  const handleExportToExcel = () => {
    // Headers
    const headerRow = [
      { value: t('ALLOCATIONS_TABLE.HEADERS.PROFESSIONAL_ID'), type: 'string' },
      { value: t('ALLOCATIONS_TABLE.HEADERS.PROFESSIONAL_NAME'), type: 'string' },
      { value: t('ALLOCATIONS_TABLE.HEADERS.FUNCTION_NAME'), type: 'string' },
      { value: t('ALLOCATIONS_TABLE.HEADERS.OFFICE'), type: 'string' },
      { value: t('ALLOCATIONS_TABLE.HEADERS.AREA'), type: 'string' },
      { value: t('ALLOCATIONS_TABLE.HEADERS.SKILLS'), type: 'string' },
      ...uniqueDates.map(date => ({ value: formatWeekHeader(date), type: 'string' }))
    ];

    // Data
    const dataRows = rows.map(row => {
      const original = row.original;
      const staticData = [
        { value: original.ProfessionalID != null ? String(original.ProfessionalID) : '', type: 'string' },
        { value: original.ProfessionalName, type: 'string' },
        { value: original.FunctionName, type: 'string' },
        { value: original.Office || '', type: 'string' },
        { value: original.area || '', type: 'string' },
        { value: original.Skills || '', type: 'string' }
      ];

      const dynamicData = uniqueDates.map(date => {
        const { name, chargeability } = getClientDataForWeek(original.ClientsByWeek, date);
        if (showChargeability) {
          return { value: `${Math.round(chargeability)}%`, type: 'string' };
        }
        return { value: name === '0' ? '' : name, type: 'string' };
      });

      return [...staticData, ...dynamicData];
    });

    generateExcel({
      filename: 'Allocations_Report.xlsx',
      sheet: { data: [headerRow, ...dataRows] }
    });
  };

  const handleClearAllFilters = () => {
    if (onClearFilters) {
      onClearFilters();
    }
    setAllFilters([]);
  };

  return (
    <div className={allocationsCss.sectionContainer}>
      <div className='allocation-toolbar'>
        <h1>Allocations</h1>
        <div className='allocation-levels' role='group' aria-label='Filter by seniority'>
          {Object.entries(filterOptions).map(([value, label]) => (
            <label key={value} title={value.replaceAll('_', ' ')}>
              <input type='checkbox' checked={selectedLevels.includes(value)} onChange={() => onLevelChange(value)} />
              {label}
            </label>
          ))}
        </div>
        <label className='allocation-project'>
          Project <input type='search' value={projectFilter} onChange={e => onProjectFilterChange(e.target.value)} placeholder='Find project…' />
        </label>
        <span className='workspace-count'>{totalAllocations} resources</span>
        <div className='allocation-actions'>
          <label>
            <input type='checkbox' checked={includeSubs} onChange={e => onIncludeSubsChange(e.target.checked)} />
            Include Subs
          </label>
          <label>
            <input type='checkbox' checked={showChargeability} onChange={e => onShowChargeabilityChange(e.target.checked)} />
            Chargeability
          </label>
          <button className='workspace-button workspace-button-quiet' onClick={handleClearAllFilters}>
            Clear filters
          </button>
          <button className='workspace-button workspace-button-secondary' onClick={handleExportToExcel}>
            <Icon name='download' size={13} />
            Export
          </button>
        </div>
      </div>
      {showChargeability && (
        <div className='allocation-legend' aria-label='Chargeability legend'>
          <span>Below 30% · Low</span>
          <span>30–70% · Partial</span>
          <span>70%+ · High</span>
        </div>
      )}
      <div className={allocationsCss.tableWrapper}>
        {data.length === 0 ? (
          <div className={allocationsCss.emptyState}>
            <p className={allocationsCss.emptyText}>No allocations found</p>
          </div>
        ) : (
          <div className={allocationsCss.tableContainer}>
            <table {...getTableProps()} className={allocationsCss.table} style={{ tableLayout: 'fixed', width: columns.reduce((sum, column) => sum + column.width, 0) }}>
              <colgroup>
                {columns.map(column => (
                  <col key={column.id || column.accessor} style={{ width: column.width }} />
                ))}
              </colgroup>
              <thead>
                <tr className={allocationsCss.headerRow}>
                  {headerGroups[0].headers.map((column, colIndex) => {
                    const isDateColumn = column.originalDate !== undefined;

                    if (!isDateColumn) {
                      return (
                        <th
                          key={colIndex}
                          className={`${allocationsCss.th} ${column.sticky ? `${allocationsCss.sticky} ${allocationsCss.headerSticky}` : ''}`}
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          style={{ width: column.width, minWidth: column.width, left: column.sticky ? (column.id === 'ProfessionalName' ? 100 : 0) : undefined }}>
                          <div style={{ textAlign: 'center' }}>
                            {column.render('Header')}
                            <span>{column.isSorted ? (column.isSortedDesc ? ' ↓' : ' ↑') : ''}</span>
                          </div>
                          {column.canFilter && (
                            <div onClick={e => e.stopPropagation()}>
                              <input
                                type='text'
                                value={column.filterValue || ''}
                                onChange={e => column.setFilter(e.target.value || undefined)}
                                placeholder='Filter…'
                                aria-label={`Filter ${column.Header}`}
                                style={{
                                  width: '100%',
                                  padding: '0.25rem',
                                  fontSize: '0.75rem',
                                  border: '1px solid #ddd',
                                  borderRadius: '3px'
                                }}
                              />
                            </div>
                          )}
                        </th>
                      );
                    }

                    const date = column.originalDate;
                    const isFirstWeek = colIndex === 0 || !headerGroups[0].headers[colIndex - 1].originalDate;

                    return (
                      <th
                        key={colIndex}
                        className={`${allocationsCss.th} ${allocationsCss.thWeek} ${isFirstWeek ? allocationsCss.weekBorderLeft : ''}`}
                        style={{ textAlign: 'center', borderRight: '1px solid #d6dfd6' }}
                        {...column.getHeaderProps(column.getSortByToggleProps())}>
                        <div style={{ textAlign: 'center' }}>
                          {formatWeekHeader(date)}
                          <span>{column.isSorted ? (column.isSortedDesc ? ' ↓' : ' ↑') : ''}</span>
                        </div>
                        {column.canFilter && (
                          <div onClick={e => e.stopPropagation()}>
                            <input
                              type='text'
                              value={column.filterValue || ''}
                              onChange={e => column.setFilter(e.target.value || undefined)}
                              placeholder='Filter…'
                              aria-label={`Filter ${column.Header}`}
                              style={{
                                width: '100%',
                                padding: '0.25rem',
                                fontSize: '0.75rem',
                                border: '1px solid #ddd',
                                borderRadius: '3px'
                              }}
                            />
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className={allocationsCss.emptyState}>
                      No resources match these filters. Clear filters to see all resources.
                    </td>
                  </tr>
                )}
                {rows.map((row, rowIndex) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} key={rowIndex} className={allocationsCss.row}>
                      {row.cells.map((cell, cellIndex) => {
                        const isSticky = cell.column.sticky === 'left';
                        const isDateColumn = cell.column.originalDate;

                        return (
                          <td
                            {...cell.getCellProps()}
                            key={cellIndex}
                            className={`${allocationsCss.td} ${isSticky ? allocationsCss.sticky : ''}`}
                            style={{
                              width: cell.column.width,
                              minWidth: cell.column.width,
                              left: isSticky ? (cell.column.id === 'ProfessionalName' ? 100 : 0) : undefined,
                              borderRight: isDateColumn ? '1px solid #e0e7df' : undefined
                            }}>
                            {cell.render('Cell')}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllocationsView;
